/**
 * The queue rail. Flagged items float to the top, then oldest first, so nothing rots at
 * the bottom of the list. Selection is a plain list with roving focus rather than a
 * table, because the reviewer moves through it with j and k far more than with a mouse.
 */
import React, { useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { QueueItem, Provenance, PROVENANCE_LABELS } from '../../../inbde/reviewTypes';
import { Eyebrow, QueueSkeleton, EmptyQueue, StatusIcon } from './primitives';

const FILTERS: { value: string; label: string }[] = [
  { value: 'ai_unreviewed', label: 'To review' },
  { value: 'in_review', label: 'Started' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'Everything' },
];

interface Props {
  items: QueueItem[];
  loading: boolean;
  selectedId: string | null;
  status: string;
  flaggedOnly: boolean;
  search: string;
  counts: Record<string, number>;
  onSelect: (id: string) => void;
  onStatus: (value: string) => void;
  onFlaggedOnly: (value: boolean) => void;
  onSearch: (value: string) => void;
}

const ReviewQueue: React.FC<Props> = ({
  items, loading, selectedId, status, flaggedOnly, search, counts,
  onSelect, onStatus, onFlaggedOnly, onSearch,
}) => {
  const filterLabel = useMemo(
    () => FILTERS.find((f) => f.value === status)?.label ?? 'this filter',
    [status],
  );

  return (
    <aside
      className="flex flex-col h-full min-h-0"
      style={{ background: 'var(--bg-tertiary)', borderRight: '1px solid var(--in-line)' }}
      aria-label="Review queue"
    >
      <div className="px-3.5 pt-3.5 pb-3 flex flex-col gap-3" style={{ borderBottom: '1px solid var(--in-line)' }}>
        <div className="flex items-center justify-between">
          <Eyebrow>Queue</Eyebrow>
          <span className="in-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
            {loading ? '' : `${items.length} shown`}
          </span>
        </div>

        <div className="relative">
          <MagnifyingGlassIcon
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: 'var(--text-tertiary)' }}
            aria-hidden="true"
          />
          <input
            type="search"
            className="in-input in-focus"
            style={{ paddingLeft: 34 }}
            placeholder="Search stems"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            aria-label="Search item stems"
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const active = status === f.value;
            const n = f.value === 'all'
              ? Object.entries(counts).filter(([k]) => k !== 'flagged').reduce((a, [, v]) => a + v, 0)
              : counts[f.value];
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => onStatus(f.value)}
                aria-pressed={active}
                className="in-focus text-[11.5px] font-medium px-2.5 py-1.5 rounded"
                style={{
                  borderRadius: 'var(--in-r-control)',
                  border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--in-line)'}`,
                  background: active ? 'var(--in-accent-50)' : 'var(--bg-secondary)',
                  color: active ? 'var(--accent-secondary)' : 'var(--text-secondary)',
                }}
              >
                {f.label}
                {typeof n === 'number' && (
                  <span className="in-mono ml-1.5" style={{ opacity: 0.7 }}>{n}</span>
                )}
              </button>
            );
          })}
        </div>

        <label className="flex items-center gap-2 text-[12px] cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            className="in-focus"
            checked={flaggedOnly}
            onChange={(e) => onFlaggedOnly(e.target.checked)}
            style={{ accentColor: 'var(--accent-primary)' }}
          />
          Only items needing discussion
          {counts.flagged > 0 && (
            <span className="in-mono text-[11px]" style={{ color: 'var(--in-flagged)' }}>{counts.flagged}</span>
          )}
        </label>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && <QueueSkeleton />}
        {!loading && items.length === 0 && (
          <EmptyQueue filterLabel={filterLabel} onReset={() => { onStatus('all'); onFlaggedOnly(false); onSearch(''); }} />
        )}
        {!loading && items.length > 0 && (
          <ul className="flex flex-col">
            {items.map((item, i) => {
              const selected = item.id === selectedId;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(item.id)}
                    aria-current={selected ? 'true' : undefined}
                    className="in-focus w-full text-left px-3.5 py-3 flex flex-col gap-1.5"
                    style={{
                      borderBottom: '1px solid var(--in-line)',
                      borderLeft: `2px solid ${selected ? 'var(--accent-primary)' : 'transparent'}`,
                      background: selected ? 'var(--bg-secondary)' : 'transparent',
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <StatusIcon value={item.provenance as Provenance} flagged={item.flagged} />
                      <span className="in-mono text-[10.5px]" style={{ color: 'var(--text-tertiary)' }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {item.is_case && (
                        <span className="in-mono text-[10px] px-1.5 py-0.5 rounded"
                          style={{ background: 'var(--in-accent-50)', color: 'var(--accent-secondary)' }}>
                          case
                        </span>
                      )}
                      {item.review_count > 0 && (
                        <span className="in-mono text-[10px] ml-auto" style={{ color: 'var(--text-tertiary)' }}>
                          {item.review_count} note{item.review_count === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <div
                      className="text-[12.5px] leading-snug"
                      style={{
                        color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}
                    >
                      {item.stem_preview || 'No stem text'}
                    </div>
                    {item.axis_a_label && (
                      <div className="text-[11px] truncate" style={{ color: 'var(--text-tertiary)' }}>
                        {item.axis_a_label}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="px-3.5 py-2.5 in-mono text-[10.5px]" style={{ borderTop: '1px solid var(--in-line)', color: 'var(--text-tertiary)' }}>
        {PROVENANCE_LABELS.ai_unreviewed}: {counts.ai_unreviewed ?? 0} left
      </div>
    </aside>
  );
};

export default ReviewQueue;
